import type { Message } from '@/types';

/**
 * ENG-4506: snapshot-reconcile for the active conversation's message list,
 * replacing fetchMessages' bare union merge. Adapted from the beforeKeys
 * design in useInteractions.reconcileFetched (ui/src/hooks/useInteractions.ts).
 *
 * A COMPLETE paginated fetch snapshot is authoritative for the conversation,
 * with two causality guards (Message has no version field, so causality is
 * tracked by observation):
 *
 *  - `beforeKeys` — message keys held at the moment the fetch was DISPATCHED.
 *    A local row absent from the snapshot is deleted only if it existed at
 *    dispatch; a row that arrived mid-flight (WS INSERT, overlapping fetch)
 *    may simply post-date the snapshot and is kept, re-validated next poll.
 *    This is the ONLY deletion path in no-WS mode (#message# REMOVEs arrive
 *    exclusively over the socket).
 *
 *  - `wsTouched` — keys the WS handler / exact-key fetch mutated within
 *    WS_TOUCH_PROTECT_MS of this fetch's dispatch, whether or not the fetch
 *    was in flight at the time (the poll snapshot is an eventually-consistent
 *    read, so a WS delivery BETWEEN polls can still out-date it; see
 *    wsTouchedDuringFetchRef in useChatWorker.ts). For those keys the local
 *    state wins outright: a
 *    present local row keeps its (fresher) version instead of being clobbered
 *    by the stale snapshot, and an absent one (WS REMOVE) is not resurrected.
 *
 * Callers guarantee: `fetched` is complete (a failed or aborted pagination
 * never reaches this function) and control messages are already filtered.
 * `fetched` need not be deduplicated — duplicate keys resolve last-wins via
 * `fetchedByKey`, matching the pre-ENG-4506 Map union.
 */
export function reconcileMessages(
  prev: Message[],
  fetched: Message[],
  beforeKeys: Set<string>,
  wsTouched: Set<string>
): Message[] {
  const fetchedByKey = new Map(fetched.map(m => [m.key, m]));
  const next: Message[] = [];
  const seen = new Set<string>();

  for (const local of prev) {
    seen.add(local.key);
    if (wsTouched.has(local.key)) {
      next.push(local); // WS write during flight is fresher than the snapshot
      continue;
    }
    const snapshot = fetchedByKey.get(local.key);
    if (snapshot) {
      // Prefer the LOCAL object when the snapshot carries nothing new so
      // unchanged rows keep referential identity (memoized row children
      // don't re-render on a no-change poll tick).
      next.push(
        snapshot.timestamp === local.timestamp &&
          snapshot.content === local.content
          ? local
          : snapshot
      );
      continue;
    }
    if (beforeKeys.has(local.key)) {
      continue; // existed at dispatch, absent from full snapshot → deleted
    }
    next.push(local); // arrived after dispatch → keep this round
  }

  for (const m of fetchedByKey.values()) {
    if (seen.has(m.key)) continue;
    if (wsTouched.has(m.key)) continue; // WS REMOVEd mid-flight → stay absent
    next.push(m);
  }

  next.sort(
    (a, b) =>
      (new Date(a.timestamp).getTime() || 0) -
      (new Date(b.timestamp).getTime() || 0)
  );

  // No-op bailout: when every output element is the corresponding prev
  // element, return prev itself so a no-change poll tick never commits a
  // referentially fresh array (React bails out of the re-render).
  if (next.length === prev.length && next.every((m, i) => m === prev[i])) {
    return prev;
  }
  return next;
}
